import React, { useEffect, useRef, useState } from "react";
import {
  Container,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Box,
  Paper,
  Button,
  Modal,
  Card,
  CardContent,
  useTheme,
} from "@mui/material";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { styled } from "@mui/system";
import { useAuth } from "../../hooks/useAuth";
import { getCurrentOrder } from "../../hooks/apiHandler";
const FlashingDot = styled("span")(({ theme, isopen }) => ({
  display: "inline-block",
  width: 10,
  height: 10,
  borderRadius: "50%",
  backgroundColor: isopen ? theme.palette.success.main : theme.palette.warning.main,
  marginRight: 8,
  animation: "blinking 1.5s infinite",
  "@keyframes blinking": {
    "0%": { opacity: 1 },
    "50%": { opacity: 0.4 },
    "100%": { opacity: 1 },
  },
}));

// Function to map order type code to a readable label and color
const getOrderTypeChip = (typeCode) => {
  switch (typeCode) {
    case 0: return { label: "In-Store", color: "info" };
    case 1: return { label: "Pickup", color: "primary" };
    case 2: return { label: "Kiosk", color: "secondary" };
    case 3: return { label: "Online", color: "warning" };
    default: return { label: "Unknown", color: "default" };
  }
};

// Function to map order status code to a label and color
const getOrderStatusChip = (statusCode) => {
  switch (statusCode) {
    case 1: return { label: "Active", color: "primary" };
    case 2: return { label: "Completed", color: "success" };
    case 5: return { label: "Cancelled", color: "error" };
    case 0: // Assuming 0 or missing means active/in progress
    default: return { label: "In Progress", color: "info" };
  }
};

// --- Order Summary Card Component (Internal) ---
const StatCard = ({ title, value, color }) => {
    const theme = useTheme();
    return (
        <Card elevation={3} sx={{ height: '100%', borderLeft: `5px solid ${theme.palette[color].main}` }}>
            <CardContent>
                <Typography variant="h6" color={theme.palette[color].dark} gutterBottom>
                    {title}
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                    {value}
                </Typography>
            </CardContent>
        </Card>
    );
};


export const Order = () => {
  const theme = useTheme();
  const { Logout } = useAuth();
  // NOTE: Using mock storeID since real one isn't available
  const storeID = useRef(JSON.parse(window.localStorage.getItem("ds")));
  const [orderData, setOrderData] = useState(null); 
  const [displayOrder, setDisplayOrder] = useState([]);
  const [reloadFlag, setReloadFlag] = useState(false);
  const [listOrderType, setListOrderType] = useState("all");
  const [cdata, setCData] = useState({
    currentOrder: 0,
    inStore: 0,
    online: 0,
    partners: 0,
    completed: 0,
    storeOpen: true, // Mock default status
  });

  const [open, setOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const handleOpen = (order) => {
    setSelectedOrder(order);
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setSelectedOrder(null);
  };

  useEffect(() => {
    
    
    async function fetchOrders() {
      try {
       const response = await getCurrentOrder({ storeID: storeID.current }, Logout);
        const data = response.data; // Using mock data for display
        
        if (data) {
          const allOrders = [...(data.completedOrders || []), ...(data.currentOrders || []), ...(data.cancelOrders || [])];
          
          const currentOrders = data.currentOrders || [];
          const completedOrders = data.completedOrders || [];
          
          // Simplified counting logic for mock data
          const inStoreCount = currentOrders.filter(o => o.type === 0).length;
          const onlineCount = currentOrders.filter(o => o.type === 3).length;

          setCData({
            currentOrder: currentOrders.length,
            inStore: inStoreCount,
            online: onlineCount,
            partners: currentOrders.filter(o => o.type === 1 || o.type === 2).length,
            completed: completedOrders.length,
            storeOpen: data.dayStatus,
          });
          setOrderData(data);
          setDisplayOrder(allOrders);
        }
      } catch (error) {
        console.error("Error fetching orders:", error);
      }
      setReloadFlag(false);
    }

    fetchOrders();
  }, [reloadFlag, Logout]); 


  const handleOrderDisplay = (e) => {
    if (!orderData) {
      console.warn("Unable to load data from Server.");
      return;
    }

    const selectedType = e.target.value;
    setListOrderType(selectedType);

    switch (selectedType) {
      case "all":
        setDisplayOrder([...orderData.completedOrders, ...orderData.currentOrders, ...orderData.cancelOrders]);
        break;
      case "cmpt":
        setDisplayOrder(orderData.completedOrders);
        break;
      case "crnt":
        setDisplayOrder(orderData.currentOrders);
        break;
      case "canc":
        setDisplayOrder(orderData.cancelOrders);
        break;
      default:
        setDisplayOrder([]);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* 📌 HEADER & STORE STATUS */}
        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 3, background: theme.palette.grey[100] }}>
            <Grid container justifyContent="space-between" alignItems="center">
                <Grid item xs={12} sm={6}>
                    <Typography variant="h5" fontWeight="bold" color="primary">
                        Daily Order Dashboard
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={6} sx={{ textAlign: { xs: 'left', sm: 'right' }, mt: { xs: 1, sm: 0 } }}>
                    <Typography variant="body1" display="inline" fontWeight="500">
                        Store Status: &nbsp;
                    </Typography>
                    <Chip
                        size="medium"
                        label={
                            <Box display="flex" alignItems="center">
                                <FlashingDot isopen={cdata.storeOpen ? 1 : 0} />
                                <Typography variant="body2" fontWeight="bold">
                                    {cdata.storeOpen ? "Open" : "Closed"}
                                </Typography>
                            </Box>
                        }
                        sx={{
                            borderColor: cdata.storeOpen ? theme.palette.success.main : theme.palette.warning.main,
                            color: cdata.storeOpen ? theme.palette.success.main : theme.palette.warning.main,
                        }}
                        variant="outlined"
                    />
                </Grid>
            </Grid>
        </Paper>

        {/* 📌 ORDER SUMMARY CARDS (Stat Cards) */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
                <StatCard title="Total Current" value={cdata.currentOrder} color="primary" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <StatCard title="Total Completed" value={cdata.completed} color="success" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <StatCard title="In-Store Orders" value={cdata.inStore} color="info" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <StatCard title="Online/Partner Orders" value={cdata.online + cdata.partners} color="secondary" />
            </Grid>
        </Grid>

        {/* 📌 ORDER FILTER SECTION & LIST */}
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
            <Grid container spacing={2} justifyContent="flex-end" alignItems="center" sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4} md={3}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Filter Orders</InputLabel>
                        <Select value={listOrderType} onChange={handleOrderDisplay} label="Filter Orders">
                            <MenuItem value="all">All Orders</MenuItem>
                            <MenuItem value="crnt">Active/Current</MenuItem>
                            <MenuItem value="cmpt">Completed</MenuItem>
                            <MenuItem value="canc">Canceled</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            {/* 📌 DISPLAY ORDERS TABLE */}
            <TableContainer component={Box} sx={{ maxHeight: 600, overflowX: "auto" }}>
                <Table stickyHeader sx={{ minWidth: 800 }}>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: theme.palette.grey[200] }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>Order No</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Amount</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Payment Status</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Date/Time</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {displayOrder.length > 0 ? (
                            displayOrder.map((order, index) => {
                                const orderType = getOrderTypeChip(order.type);
                                const orderStatus = getOrderStatusChip(order.oStatus);
                                const paymentSuccessful = order.payment && order.payment.length > 0;
                                const total = order.total ?? 0;
                                
                                return (
                                    <TableRow 
                                        key={index} 
                                        hover 
                                        sx={{ 
                                            backgroundColor: order.oStatus === 5 ? theme.palette.error.light + '15' : 'inherit',
                                            '&:hover': { backgroundColor: theme.palette.grey[50] },
                                        }}
                                    >
                                        <TableCell>{order.orderNo}</TableCell>
                                        <TableCell>
                                            <Chip label={orderType.label} color={orderType.color} size="small" variant="outlined" />
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={orderStatus.label} color={orderStatus.color} size="small" />
                                        </TableCell>
                                        <TableCell align="right">${total.toFixed(2)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={paymentSuccessful ? "Successful" : "Pending"}
                                                color={paymentSuccessful ? "success" : "primary"}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>{new Date(order.startTime).toLocaleString()}</TableCell>
                                        <TableCell>
                                            <Button onClick={() => handleOpen(order)} variant="outlined" color="primary" size="small">
                                                View
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    <Typography variant="body1" color="textSecondary" sx={{ py: 2 }}>
                                        No Orders to Display based on the current filter.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>

      {/* This Model View.. */}
      <Modal open={open} onClose={handleClose} aria-labelledby="order-details-modal">
        <Box
          sx={{
            position: "absolute",
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: '80%', md: '600px' },
            maxHeight: '90vh',
            overflowY: 'auto',
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2
          }}
        >
          <Typography id="order-details-modal" variant="h5" component="h2" sx={{ mb: 2, borderBottom: '1px solid #ccc' }}>
            Order Details: {selectedOrder?.orderNo}
          </Typography>

          {selectedOrder && (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {/* Order Meta Info */}
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Date & Time</Typography>
                  <Typography>{new Date(selectedOrder.startTime).toLocaleString()}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  <Chip label={getOrderStatusChip(selectedOrder.oStatus).label} color={getOrderStatusChip(selectedOrder.oStatus).color} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Order Type</Typography>
                  <Chip label={getOrderTypeChip(selectedOrder.type).label} color={getOrderTypeChip(selectedOrder.type).color} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Total Amount</Typography>
                  <Typography variant="h6" color="success.dark">${selectedOrder.total?.toFixed(2) ?? '0.00'}</Typography>
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Customer & Staff Info</Typography>
              <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        {selectedOrder.custInfo?.name && <Typography><b>Name:</b> {selectedOrder.custInfo.name}</Typography>}
                        {selectedOrder.custInfo?.phone && <Typography><b>Phone:</b> {selectedOrder.custInfo.phone}</Typography>}
                        {selectedOrder.custInfo?.address && <Typography><b>Address:</b> {selectedOrder.custInfo.address}</Typography>}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        {selectedOrder.orders?.length > 0 ? selectedOrder.orders.map((orderItem, orderIndex) => (
                            <Typography key={'staff' + orderIndex}><b>Staff:</b> {orderItem.cName}</Typography>
                        )) : null}
                        <Typography><b>Payment:</b> {selectedOrder.payment?.length > 0 ? 'Successful' : 'Pending'}</Typography>
                    </Grid>
                </Grid>
              </Paper>
              
              <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Ordered Items</Typography>
              <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: theme.palette.grey[200] }}>
                    <TableRow>
                      <TableCell><b>Item Name</b></TableCell>
                      <TableCell align="right"><b>Quantity</b></TableCell>
                      <TableCell align="right"><b>Price</b></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedOrder.orders?.map((orderItem, orderIndex) => (
                      orderItem.order.map((item, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>
                            {item.item}
                            {item.comment && <Typography variant="caption" display="block" color="textSecondary">// {item.comment}</Typography>}
                          </TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">${(item.quantity * item.price).toFixed(2)}</TableCell>
                        </TableRow>
                      ))
                    ))}
                    {!selectedOrder.orders?.some(o => o.order?.length > 0) && (
                        <TableRow><TableCell colSpan={3} align="center">No Items Found</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          <Button
            variant="contained"
            color="primary"
            sx={{ mt: 3 }}
            onClick={handleClose}
          >
            Close
          </Button>
        </Box>
      </Modal>
    </Container>
  );
};
